/**
 * The component layer that was missing between the tokens and the screens.
 *
 * 🔴 The gap this closes. The design had tokens and about ten primitives and
 * then nothing, so every view re-improvised: ten bare `Loading…` paragraphs in
 * four different wordings, not one skeleton anywhere, and a single empty-state
 * primitive — `.notyet`, a dashed "not ported yet" box — which AlertsView and
 * Activity both had to inline-override with `borderStyle: 'solid'` to stop good
 * news reading as an unfinished feature.
 *
 * Two of those overrides existed in the tree. That is the tell: when a screen
 * has to fight a primitive to say something ordinary, the primitive is missing,
 * not the screen.
 */

import { X } from 'lucide-react';
import type React from 'react';

import { Button } from './components/Button.tsx';

/**
 * A table-shaped skeleton, not a sentence.
 *
 * A dense ten-column table popping into existence out of a one-line "Loading…"
 * is the cheapest-feeling moment in the product and it happens on every screen.
 * The skeleton holds roughly the space the content will take, so the page does
 * not jump when it arrives — which is the actual complaint behind "it feels
 * slow" far more often than the request itself is.
 *
 * `rows` and `cols` default to a middling table. Callers that know better should
 * say so; callers that do not should not have to think about it.
 */
export function Loading({
  rows = 5,
  cols = 4,
  label = 'Loading',
}: {
  rows?: number;
  cols?: number;
  label?: string;
}): React.JSX.Element {
  return (
    <div
      className="skeleton"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
    >
      {Array.from({ length: rows }, (_, r) => (
        <div className="skeleton-row" key={r}>
          {Array.from({ length: cols }, (_, c) => (
            // Widths vary per column so it reads as a table rather than a grid
            // of identical bars. The first column is wider because in every
            // table here it holds a name.
            <span
              className="skeleton-cell"
              key={c}
              style={{ flex: c === 0 ? 2.2 : 1, opacity: 1 - r * 0.12 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * An empty state that is distinct from a NOT-PORTED state.
 *
 * Collapsing the two is why `AlertsView` had to override the dashed border to
 * stop "no open alerts" — the best news the screen can carry — from looking
 * like an unfinished feature.
 */
export function Empty({
  title,
  body,
  action,
  tone = 'neutral',
}: {
  title: string;
  body?: string;
  action?: React.ReactNode;
  /** `good` for an empty state that is a RESULT rather than an absence. */
  tone?: 'neutral' | 'good';
}): React.JSX.Element {
  return (
    <div className={tone === 'good' ? 'empty empty-good' : 'empty'}>
      <strong>{title}</strong>
      {body !== undefined && <p className="small muted">{body}</p>}
      {action}
    </div>
  );
}

/**
 * A labelled form field.
 *
 * The label is bound with htmlFor/id rather than by wrapping, because a wrapped
 * label with a nested control is exactly the pattern the routing screen already
 * gets wrong — `.field` is a form-row class and using it as a label wrapper is
 * why that checkbox list stacks oddly.
 *
 * `hint` sits UNDER the control, not in a placeholder. A placeholder disappears
 * the moment somebody types, which is precisely when the rule about the value
 * is needed, and it is invisible to a screen reader that has already moved on.
 */
export function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint !== undefined && error == null && <p className="field-hint small muted">{hint}</p>}
      {error != null && (
        // role="alert" so the failure is announced rather than only drawn. A
        // form that reports errors visually alone is one a screen reader user
        // submits repeatedly without ever learning why it failed.
        <p className="field-error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A transient confirmation.
 *
 * 🔴 Deliberately NOT the whole feedback mechanism. This codebase's history has
 * a green toast over a silent empty ACL in it — the screen said "saved" and the
 * customer lost every site. A toast confirms what a screen ALREADY shows; it
 * must never be the only evidence that something happened.
 *
 * So it carries a tone, and a failure toast does not fade: an error nobody read
 * is an error that did not happen, as far as the operator is concerned.
 */
export function Toast({
  message,
  tone,
  onDismiss,
}: {
  message: string;
  tone: 'ok' | 'fault';
  onDismiss: () => void;
}): React.JSX.Element {
  return (
    <div className={`toast toast-${tone}`} role={tone === 'fault' ? 'alert' : 'status'}>
      <span>{message}</span>
      <Button variant="ghost" size="sm" iconOnly icon={<X size={14} />} onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
